import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Download, ChevronDown, Calendar, RefreshCw, Plus, Link, Unlink, CheckCircle, AlertCircle, Building2, CreditCard, Wallet, PiggyBank, TrendingUp, X, Pencil, Check, Trash2, Loader2 } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis } from 'recharts';
import { toast } from 'sonner';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

// Plaid Link Button Component - Demo Mode
const PlaidLinkButton = ({ onSuccess, buttonText = "Link Bank Account", className = "" }) => {
  const [loading, setLoading] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);

  const demoInstitutions = [
    { id: 'chase', name: 'Chase', icon: '🏦', color: 'from-blue-600 to-blue-800' },
    { id: 'bofa', name: 'Bank of America', icon: '🏛️', color: 'from-red-600 to-red-800' },
    { id: 'wells', name: 'Wells Fargo', icon: '🏦', color: 'from-yellow-600 to-red-700' },
    { id: 'citi', name: 'Citibank', icon: '🏦', color: 'from-blue-500 to-blue-700' },
    { id: 'capital_one', name: 'Capital One', icon: '💳', color: 'from-red-500 to-orange-600' },
    { id: 'amex', name: 'American Express', icon: '💳', color: 'from-blue-400 to-cyan-600' },
  ];

  const handleDemoConnect = async (institution) => {
    setLoading(true);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Generate demo account data
    const demoAccounts = [
      {
        account_id: `${institution.id}_checking_${Date.now()}`,
        name: `${institution.name} Checking`,
        type: 'depository',
        subtype: 'checking',
        balances: {
          current: Math.round(Math.random() * 50000 + 5000),
          available: Math.round(Math.random() * 45000 + 4000),
          iso_currency_code: 'USD'
        }
      },
      {
        account_id: `${institution.id}_savings_${Date.now()}`,
        name: `${institution.name} Savings`,
        type: 'depository',
        subtype: 'savings',
        balances: {
          current: Math.round(Math.random() * 100000 + 10000),
          available: Math.round(Math.random() * 100000 + 10000),
          iso_currency_code: 'USD'
        }
      }
    ];

    // Call success handler with demo data
    onSuccess(null, {
      institution: { name: institution.name, institution_id: institution.id },
      accounts: demoAccounts,
      isDemo: true
    });

    setShowDemoModal(false);
    setLoading(false);
  };

  return (
    <>
      <button
        onClick={() => setShowDemoModal(true)}
        disabled={loading}
        className={`flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-4 py-2 rounded-xl font-medium transition-colors ${className}`}
        data-testid="plaid-link-button"
      >
        {loading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
        {loading ? 'Connecting...' : buttonText}
      </button>

      {/* Demo Bank Selection Modal */}
      {showDemoModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowDemoModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white">Connect Bank Account</h3>
                  <p className="text-gray-400 text-sm">Demo Mode - Select a bank to simulate connection</p>
                </div>
                <button onClick={() => setShowDemoModal(false)} className="p-2 hover:bg-gray-800 rounded-lg">
                  <X size={18} className="text-gray-400" />
                </button>
              </div>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              {demoInstitutions.map((inst) => (
                <button
                  key={inst.id}
                  onClick={() => handleDemoConnect(inst)}
                  disabled={loading}
                  className="flex items-center gap-3 p-4 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-cyan-500 rounded-xl transition-all disabled:opacity-50"
                >
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${inst.color} flex items-center justify-center text-lg`}>
                    {inst.icon}
                  </div>
                  <span className="text-white text-sm font-medium">{inst.name}</span>
                </button>
              ))}
            </div>
            <div className="p-4 border-t border-gray-700 bg-gray-800/50 rounded-b-2xl">
              <p className="text-gray-500 text-xs text-center">
                🔒 Demo mode simulates bank connection with sample data
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Bank/Financial Institution Connections Component
const ConnectionsPanel = ({ connections, setConnections, onAccountsUpdate }) => {
  const [syncingItemId, setSyncingItemId] = useState(null);

  const handlePlaidSuccess = async (publicToken, metadata) => {
    // Demo mode - use the demo data directly
    const demoAccounts = metadata.accounts || [];
    const totalBalance = demoAccounts.reduce((sum, acc) => sum + (acc.balances?.current || 0), 0);
    
    // Add to connections
    const newConnection = {
      id: Date.now().toString(),
      name: metadata.institution?.name || 'Bank Account',
      icon: '🏦',
      type: 'bank',
      status: 'connected',
      lastSync: new Date().toLocaleString(),
      accounts: demoAccounts,
      totalBalance,
      institutionId: metadata.institution?.institution_id,
      isDemo: metadata.isDemo || false
    };

    const updatedConnections = [...connections, newConnection];
    setConnections(updatedConnections);
    localStorage.setItem('financeConnections', JSON.stringify(updatedConnections));
    
    toast.success(`${metadata.institution?.name || 'Bank'} connected successfully!`);
    
    if (onAccountsUpdate) {
      onAccountsUpdate(demoAccounts);
    }
  };

  const syncAccounts = async (connectionId) => {
    setSyncingItemId(connectionId);
    
    // Simulate sync delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Update balances with slight random changes to simulate real sync
    setConnections(prev => prev.map(conn => {
      if (conn.id === connectionId) {
        const updatedAccounts = conn.accounts.map(acc => ({
          ...acc,
          balances: {
            ...acc.balances,
            current: acc.balances.current + Math.round((Math.random() - 0.5) * 500),
            available: acc.balances.available + Math.round((Math.random() - 0.5) * 400)
          }
        }));
        const totalBalance = updatedAccounts.reduce((sum, acc) => sum + (acc.balances?.current || 0), 0);
        return {
          ...conn,
          accounts: updatedAccounts,
          totalBalance,
          lastSync: new Date().toLocaleString()
        };
      }
      return conn;
    }));
    
    toast.success('Accounts synced successfully');
    setSyncingItemId(null);
  };

  const disconnectAccount = (accountId) => {
    const updatedConnections = connections.filter(c => c.id !== accountId);
    setConnections(updatedConnections);
    localStorage.setItem('financeConnections', JSON.stringify(updatedConnections));
    toast.success('Account disconnected');
  };

  return (
    <div className="bg-black border border-gray-700 rounded-2xl p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Link size={18} className="text-cyan-400" />
            Connected Accounts
          </h3>
          <p className="text-gray-500 text-sm">Link your banks, cards, and investment accounts</p>
        </div>
        <PlaidLinkButton 
          onSuccess={handlePlaidSuccess}
          buttonText="+ Add Connection"
        />
      </div>

      {connections.length > 0 ? (
        <div className="space-y-3">
          {connections.map((conn) => (
            <div key={conn.id} className="flex items-center justify-between p-4 bg-gray-900/50 rounded-xl border border-gray-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-lg">
                  {conn.icon || '🏦'}
                </div>
                <div>
                  <div className="text-white font-medium">{conn.name}</div>
                  <div className="text-green-400 text-xs flex items-center gap-1">
                    <CheckCircle size={10} />
                    Connected
                  </div>
                  {conn.totalBalance !== undefined && (
                    <div className="text-cyan-400 text-sm font-semibold">
                      ${conn.totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                  )}
                </div>
                {conn.lastSync && (
                  <div className="text-gray-500 text-xs ml-4">
                    Last synced: {conn.lastSync}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => syncAccounts(conn.id)}
                  disabled={syncingItemId === conn.id}
                  className="p-2 text-gray-400 hover:text-cyan-400 hover:bg-gray-800 rounded-lg transition-colors"
                  title="Sync transactions"
                >
                  <RefreshCw size={16} className={syncingItemId === conn.id ? 'animate-spin' : ''} />
                </button>
                <button
                  onClick={() => disconnectAccount(conn.id)}
                  className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded-lg transition-colors"
                  title="Disconnect"
                >
                  <Unlink size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-900/50 rounded-xl border border-dashed border-gray-700">
          <Wallet size={32} className="mx-auto text-gray-600 mb-2" />
          <p className="text-gray-400">No accounts connected</p>
          <p className="text-gray-500 text-sm mt-1">Connect your bank or card to sync transactions</p>
        </div>
      )}
    </div>
  );
};
const RealEstateManagingPartnerFinances = () => {
  const navigate = useNavigate();
  const [selectedTransactionFilter, setSelectedTransactionFilter] = useState('All');
  const [upcomingBills, setUpcomingBills] = useState([]);
  const [loadingBills, setLoadingBills] = useState(true);
  const [lastBillsSync, setLastBillsSync] = useState(null);
  
  // Financial connections state
  const [connections, setConnections] = useState(() => {
    const saved = localStorage.getItem('financeConnections');
    return saved ? JSON.parse(saved) : [];
  });

  // Default transaction filter categories
  const defaultTransactionFilters = [
    { id: 'All', name: 'All', icon: '📊' },
    { id: 'Income', name: 'Income', icon: '💰' },
    { id: 'Equipment', name: 'Equipment', icon: '🔧' },
    { id: 'Software', name: 'Software', icon: '💻' }
  ];

  // Load custom transaction filters from localStorage
  const [customTransactionFilters, setCustomTransactionFilters] = useState(() => {
    try {
      const saved = localStorage.getItem('finance_transaction_filters');
      return saved ? JSON.parse(saved) : defaultTransactionFilters;
    } catch { return defaultTransactionFilters; }
  });

  // Modal states for editing transaction categories
  const [showTransactionFilterModal, setShowTransactionFilterModal] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');
  const [newFilterIcon, setNewFilterIcon] = useState('📋');
  const [editingFilter, setEditingFilter] = useState(null);
  const [editFilterValue, setEditFilterValue] = useState('');

  const filterIcons = ['📋', '💰', '🔧', '💻', '📊', '📈', '💳', '🏦', '💵', '🎨', '📦', '🚗', '🏠', '📱', '🍔', '✈️', '🎬', '💡', '🔌', '📝'];

  // Persist custom filters to localStorage
  useEffect(() => {
    localStorage.setItem('finance_transaction_filters', JSON.stringify(customTransactionFilters));
  }, [customTransactionFilters]);

  // Transaction filter CRUD operations
  const addTransactionFilter = () => {
    if (!newFilterName.trim()) return;
    if (customTransactionFilters.some(f => f.id === newFilterName)) { 
      toast.error('Filter already exists'); 
      return; 
    }
    const newFilter = { id: newFilterName, name: newFilterName, icon: newFilterIcon };
    const updated = [...customTransactionFilters, newFilter];
    setCustomTransactionFilters(updated);
    setNewFilterName('');
    setNewFilterIcon('📋');
    toast.success(`Added "${newFilterName}" filter`);
  };

  const updateTransactionFilter = (oldId, newName) => {
    if (!newName.trim()) return;
    const updated = customTransactionFilters.map(f => f.id === oldId ? { ...f, id: newName, name: newName } : f);
    setCustomTransactionFilters(updated);
    if (selectedTransactionFilter === oldId) setSelectedTransactionFilter(newName);
    setEditingFilter(null);
    setEditFilterValue('');
    toast.success(`Filter renamed to "${newName}"`);
  };

  const deleteTransactionFilter = (filterId) => {
    if (['All', 'Income'].includes(filterId)) { 
      toast.error('Cannot delete default filter'); 
      return; 
    }
    const updated = customTransactionFilters.filter(f => f.id !== filterId);
    setCustomTransactionFilters(updated);
    if (selectedTransactionFilter === filterId) setSelectedTransactionFilter('All');
    toast.success('Filter deleted');
  };

  // API URL - use same origin
  const apiUrl = typeof window !== 'undefined' ? window.location.origin : '';

  // Save connections to localStorage
  useEffect(() => {
    localStorage.setItem('financeConnections', JSON.stringify(connections));
  }, [connections]);

  // Fetch bills from backend on mount
  useEffect(() => {
    fetchBills();
  }, []);

  const fetchBills = async () => {
    setLoadingBills(true);
    try {
      const response = await fetch(`${apiUrl}/api/excel/get-bills`);
      const data = await response.json();
      
      if (data.status === 'success' && data.bills && data.bills.length > 0) {
        setUpcomingBills(data.bills);
        setLastBillsSync(new Date());
        console.log(`✅ Loaded ${data.bills.length} bills from backend`);
      } else {
        // Use default bills if no data from backend
        setUpcomingBills(defaultBills);
        console.log('📋 Using default bills (no synced data found)');
      }
    } catch (error) {
      console.error('Error fetching bills:', error);
      setUpcomingBills(defaultBills);
    } finally {
      setLoadingBills(false);
    }
  };

  // Default bills (shown when no synced data)
  const defaultBills = [
    {
      id: 1,
      name: 'Studio Rent',
      due_date: '2026-01-01',
      amount: 1200.00,
      status: 'upcoming',
      icon: '🏢'
    },
    {
      id: 2,
      name: 'Adobe Creative Cloud',
      due_date: '2026-01-05',
      amount: 54.99,
      status: 'upcoming',
      icon: '🎨'
    },
    {
      id: 3,
      name: 'Equipment Insurance',
      due_date: '2026-01-15',
      amount: 450.00,
      status: 'upcoming',
      icon: '📷'
    },
    {
      id: 4,
      name: 'Website Hosting',
      due_date: '2026-01-20',
      amount: 29.99,
      status: 'upcoming',
      icon: '🌐'
    },
    {
      id: 5,
      name: 'Phone Bill',
      due_date: '2026-01-25',
      amount: 85.00,
      status: 'upcoming',
      icon: '📱'
    },
  ];

  // Monthly income data for small sparkline charts
  const monthlyIncomeData = [
    { month: 'Jan', value: 8500 },
    { month: 'Feb', value: 9200 },
    { month: 'Mar', value: 10800 },
    { month: 'Apr', value: 11200 },
    { month: 'May', value: 13500 },
    { month: 'Jun', value: 12550 },
  ];

  // Monthly expenses data for small sparkline charts
  const monthlyExpensesData = [
    { month: 'Jan', value: 4200 },
    { month: 'Feb', value: 4800 },
    { month: 'Mar', value: 5100 },
    { month: 'Apr', value: 4900 },
    { month: 'May', value: 5800 },
    { month: 'Jun', value: 5480 },
  ];

  // Recent transactions
  const recentTransactions = [
    { 
      id: 1, 
      name: 'Martinez Wedding - Final Payment', 
      date: '2025-12-28', 
      client: 'Emily Martinez',
      amount: 2250.00, 
      type: 'income',
      category: 'Income'
    },
    { 
      id: 2, 
      name: 'Chen Corporate Event Payment', 
      date: '2025-12-27', 
      client: 'Tech Corp',
      amount: 3800.00, 
      type: 'income',
      category: 'Income'
    },
    { 
      id: 3, 
      name: 'Adobe Creative Cloud', 
      date: '2025-12-26', 
      client: 'Adobe Inc',
      amount: 54.99, 
      type: 'expense',
      category: 'Software'
    },
    { 
      id: 4, 
      name: 'Equipment Insurance - Quarterly', 
      date: '2025-12-26', 
      client: 'Photography Insurance Co',
      amount: 450.00, 
      type: 'expense',
      category: 'Equipment'
    },
  ];

  const transactionFilters = customTransactionFilters.map(f => f.id);

  const filteredTransactions = selectedTransactionFilter === 'All' 
    ? recentTransactions 
    : recentTransactions.filter(t => t.category === selectedTransactionFilter);

  // Format date helper
  const formatDueDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-1">Finances</h1>
        <p className="text-gray-400 text-sm">Manage your financial overview</p>
      </div>

      {/* Connections Panel */}
      <ConnectionsPanel
        connections={connections}
        setConnections={setConnections}
        onAccountsUpdate={(accounts) => {
          // Update balance displays when accounts are synced
          if (accounts && accounts.length > 0) {
            const totalBalance = accounts.reduce((sum, acc) => sum + (acc.balances?.current || 0), 0);
            // Store in localStorage for persistence
            localStorage.setItem('plaidAccountData', JSON.stringify({ accounts, totalBalance }));
          }
        }}
      />

      {/* Top Row - Balance, Income, Expenses */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* My Balance Card - Purple/Pink Gradient */}
        <div className="bg-gradient-to-br from-purple-600 via-pink-500 to-pink-400 rounded-2xl p-5 relative overflow-hidden">
          <div className="flex items-center justify-between mb-1">
            <span className="text-white/80 text-sm">My Balance</span>
            <button className="flex items-center gap-1 bg-white/20 hover:bg-white/30 rounded-lg px-2 py-1 text-xs transition-colors">
              USD <ChevronDown size={14} />
            </button>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-1">$94,320.75</h2>
          <p className="text-white/70 text-sm mb-4">Available for use</p>
          
          <div className="flex gap-3">
            <button className="flex-1 bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors">
              <Send size={16} />
              Send
            </button>
            <button className="flex-1 bg-purple-700/50 hover:bg-purple-700/70 text-white font-semibold py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 border border-white/20 transition-colors">
              <Download size={16} />
              Request
            </button>
          </div>
        </div>

        {/* Monthly Income Card */}
        <div className="bg-black border border-gray-700 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Monthly Income</span>
            <span className="text-green-400 text-sm font-semibold">↗ +12.5%</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-4">$12,550.00</h2>
          
          {/* Mini Line Chart */}
          <div className="h-16">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyIncomeData}>
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#A855F7" 
                  strokeWidth={2}
                  dot={false}
                />
                <XAxis 
                  dataKey="month" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: '#6B7280' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Monthly Expenses Card */}
        <div className="bg-black border border-gray-700 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Monthly Expenses</span>
            <span className="text-red-400 text-sm font-semibold">↘ +8.2%</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-4">$5,480.45</h2>
          
          {/* Mini Line Chart */}
          <div className="h-16">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyExpensesData}>
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#A855F7" 
                  strokeWidth={2}
                  dot={false}
                />
                <XAxis 
                  dataKey="month" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: '#6B7280' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Bottom Row - Recent Transactions & Upcoming Bills */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Recent Transactions Section */}
        <div className="bg-black border border-gray-700 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Recent Transactions</h3>
            <button 
              onClick={() => navigate('/all-transactions')}
              className="text-orange-400 hover:text-orange-300 text-sm font-semibold transition-colors"
            >
              View all
            </button>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 mb-4 flex-wrap items-center">
            {customTransactionFilters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setSelectedTransactionFilter(filter.id)}
                data-testid={`filter-${filter.id.toLowerCase()}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  selectedTransactionFilter === filter.id
                    ? 'bg-cyan-500 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                <span>{filter.icon}</span>
                {filter.name}
              </button>
            ))}
            {/* Edit Categories Button */}
            <button
              onClick={() => setShowTransactionFilterModal(true)}
              data-testid="edit-transaction-categories-btn"
              className="px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap bg-transparent border border-dashed border-gray-500 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-1"
              title="Edit Categories"
            >
              <Pencil size={14} />
              Edit
            </button>
          </div>

          {/* Transactions List */}
          <div className="space-y-3">
            {filteredTransactions.map((transaction) => (
              <div 
                key={transaction.id} 
                className="flex items-center justify-between py-3 border-b border-gray-800 last:border-0"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    transaction.type === 'income' 
                      ? 'bg-orange-500/20' 
                      : 'bg-gray-700'
                  }`}>
                    {transaction.type === 'income' ? (
                      <svg className="w-5 h-5 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 2a8 8 0 100 16 8 8 0 000-16zM8 12a1 1 0 01-1-1V9a1 1 0 012 0v2a1 1 0 01-1 1zm4 0a1 1 0 01-1-1V9a1 1 0 112 0v2a1 1 0 01-1 1z"/>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6z" clipRule="evenodd"/>
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">{transaction.name}</p>
                    <p className="text-gray-500 text-xs">{transaction.date} • {transaction.client}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${
                    transaction.type === 'income' ? 'text-green-400' : 'text-white'
                  }`}>
                    {transaction.type === 'income' ? '+' : '-'}${transaction.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-gray-500 text-xs">Completed</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Bills Section */}
        <div className="bg-black border border-gray-700 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Upcoming Bills</h3>
            <div className="flex items-center gap-2">
              <button 
                onClick={fetchBills}
                className="text-gray-400 hover:text-white transition-colors p-1"
                title="Refresh bills"
              >
                <RefreshCw size={16} className={loadingBills ? 'animate-spin' : ''} />
              </button>
              <button 
                onClick={() => navigate('/all-bills')}
                className="text-orange-400 hover:text-orange-300 text-sm font-semibold transition-colors"
              >
                View all
              </button>
            </div>
          </div>

          {/* Bills List */}
          <div className="space-y-3">
            {loadingBills ? (
              <div className="text-center py-8 text-gray-500">
                <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                <p>Loading bills...</p>
              </div>
            ) : upcomingBills.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No bills found</p>
                <p className="text-xs mt-1">Run the sync script to import bills</p>
              </div>
            ) : (
              upcomingBills.map((bill) => (
                <div 
                  key={bill.id} 
                  className="flex items-center justify-between py-3 border-b border-gray-800 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center text-lg">
                      {bill.icon || '📄'}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{bill.name}</p>
                      <div className="flex items-center gap-1 text-gray-500 text-xs">
                        <Calendar size={12} />
                        <span>Due {formatDueDate(bill.due_date || bill.dueDate)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-semibold">
                      ${(bill.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                    <p className={`text-xs ${bill.status === 'paid' ? 'text-green-400' : bill.status === 'overdue' ? 'text-red-400' : 'text-yellow-400'}`}>
                      {bill.status === 'paid' ? 'Paid' : bill.status === 'overdue' ? 'Overdue' : 'Pending'}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Total Upcoming */}
          {upcomingBills.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">Total Upcoming</span>
                <span className="text-white font-bold text-lg">
                  ${upcomingBills.reduce((sum, bill) => sum + (bill.amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Transaction Filter Categories Modal */}
      {showTransactionFilterModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowTransactionFilterModal(false)}>
          <div className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Edit Transaction Categories</h3>
              <button onClick={() => setShowTransactionFilterModal(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={18} className="text-gray-400" /></button>
            </div>
            <div className="p-4 border-b border-gray-700">
              <div className="flex gap-2 mb-3">
                <div className="relative">
                  <button className="w-10 h-10 bg-black border border-gray-600 rounded-lg text-xl flex items-center justify-center" onClick={(e) => e.currentTarget.nextElementSibling.classList.toggle('hidden')}>{newFilterIcon}</button>
                  <div className="hidden absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 z-10 grid grid-cols-5 gap-1 w-40">
                    {filterIcons.map(icon => (<button key={icon} onClick={(e) => { setNewFilterIcon(icon); e.currentTarget.parentElement.classList.add('hidden'); }} className="w-7 h-7 hover:bg-gray-700 rounded text-lg flex items-center justify-center">{icon}</button>))}
                  </div>
                </div>
                <input type="text" value={newFilterName} onChange={(e) => setNewFilterName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addTransactionFilter()} placeholder="New category name..." className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" data-testid="new-transaction-filter-input" />
              </div>
              <button onClick={addTransactionFilter} disabled={!newFilterName.trim()} className="w-full px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5" data-testid="add-transaction-filter-btn"><Plus size={14} />Add Category</button>
            </div>
            <div className="p-4 max-h-80 overflow-y-auto space-y-2">
              {customTransactionFilters.map(cat => (
                <div key={cat.id} className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group">
                  {editingFilter === cat.id ? (
                    <>
                      <span className="text-lg">{cat.icon}</span>
                      <input type="text" value={editFilterValue} onChange={(e) => setEditFilterValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') updateTransactionFilter(cat.id, editFilterValue); if (e.key === 'Escape') { setEditingFilter(null); setEditFilterValue(''); }}} className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm" autoFocus />
                      <button onClick={() => updateTransactionFilter(cat.id, editFilterValue)} className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"><Check size={14} /></button>
                      <button onClick={() => { setEditingFilter(null); setEditFilterValue(''); }} className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <span className="w-8 h-8 rounded-lg bg-gray-700 flex items-center justify-center text-sm">{cat.icon}</span>
                      <span className="flex-1 text-white text-sm">{cat.name}{['All', 'Income'].includes(cat.id) && <span className="ml-2 text-xs text-gray-500">(default)</span>}</span>
                      <button onClick={() => { setEditingFilter(cat.id); setEditFilterValue(cat.name); }} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100"><Pencil size={14} /></button>
                      {!['All', 'Income'].includes(cat.id) && <button onClick={() => deleteTransactionFilter(cat.id)} className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>}
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-gray-700">
              <button onClick={() => setShowTransactionFilterModal(false)} className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium" data-testid="done-transaction-filter-btn">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealEstateManagingPartnerFinances;
